import React, { useEffect, useState, useRef } from 'react';
import { useAnalytics } from '../../../utils/analytics';
import { BaseStepPropsV2 } from '../../common/v2/BaseStepV2.types';
import { StepV2ConfirmButton } from '../../common/v2/StepV2ConfirmButton';
import { MainButtonVariant } from '../../styledComponent/Buttons.style';
import {
  CopingWithNarcissismScreen,
  CopingWithNarcissismScreenContentWrapper,
  CopingWithNarcissismScreenCtaWrapper,
  CopingWithNarcissismScreenDescription,
  CopingWithNarcissismScreenTexts,
  CopingWithNarcissismScreenTitle,
} from './CopingWithNarcissism.style';
import { AbsoluteHeader } from '../../common/AbsoluteHeader/AbsoluteHeader';
import { useMst } from '../../../models/rootModel';
import { BaseStepV3 } from '../../common/v3/BaseStepV3';
import gsap from 'gsap';
import { useIsMobileWebview } from '../../../hooks/useIsMobileWebview.hook';

interface CopingWithNarcissismStepProps extends BaseStepPropsV2 {
  title: string;
  secondTitle: string;
  description: string;
  ctaButtonTitle: string;
  backgroundImageUrl: string;
  mobileViewBackgroundImageUrl: string;
  nextStepId?: string;
}

export const CopingWithNarcissism: React.FC<CopingWithNarcissismStepProps> = (props) => {
  const {
    nextStep,
    goToNamedStep,
    ctaButtonTitle,
    isActive,
    title,
    description,
    backgroundImageUrl,
    mobileViewBackgroundImageUrl,
    previousStep,
    nextStepId,
    stepName,
    secondTitle,
  } = props;

  const analytics = useAnalytics();
  const { UserDiscoveryResponse } = useMst();
  const [firstCtaClick, setFirstCtaClick] = useState(false);

  const isMobileWebview = useIsMobileWebview();

  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const ctaWrapperRef = useRef(null);

  useEffect(() => {
    if (isActive) {
      analytics.track(`view copingWithNarcissism`, {
        stepName,
        segment: UserDiscoveryResponse.segment,
        isMobileWebview,
      });

      window.scrollTo(0, 0);

      //Animations
      const ctx = gsap.context(() => {
        const tl = gsap.timeline();

        tl.from(titleRef.current, {
          opacity: 0,
          y: 100,
          duration: 1.5,
          ease: 'power2.out',
        });

        tl.from(
          descriptionRef.current,
          {
            opacity: 0,
            y: 100,
            duration: 1.5,
            ease: 'power2.out',
          },
          '-=0.75'
        );

        tl.fromTo(
          ctaWrapperRef.current,
          {
            opacity: 0,
            pointerEvents: 'none',
          },
          {
            opacity: 1,
            pointerEvents: 'auto',
            delay: 0,
            duration: 1,
            ease: 'power2.out',
          }
        );
      });

      return () => ctx.kill();
    }
  }, [analytics, isActive, UserDiscoveryResponse.segment, stepName, isMobileWebview]);

  const handleOnClick = () => {
    analytics.track(`click copingWithNarcissism`, {
      stepName,
      segment: UserDiscoveryResponse.segment,
      ctaOrder: firstCtaClick ? 2 : 1,
      isMobileWebview,
    });

    if (!firstCtaClick) {
      gsap.context(() => {
        const tl = gsap.timeline({});

        tl.to(titleRef.current, {
          opacity: 0,
          duration: 1,
          ease: 'power2.out',
        });

        tl.to(
          descriptionRef.current,
          {
            opacity: 0,
            duration: 1,
            ease: 'power2.out',
          },
          '<'
        );

        tl.to(
          ctaWrapperRef.current,
          {
            opacity: 0,
            duration: 1,
            ease: 'power2.out',
            onComplete: () => {
              setFirstCtaClick(true);

              tl.to(titleRef.current, {
                opacity: 1,
                duration: 0.5,
                ease: 'power2.out',
              });

              tl.to(descriptionRef.current, {
                opacity: 1,
                duration: 0.5,
                ease: 'power2.out',
              });

              tl.to(ctaWrapperRef.current, {
                opacity: 1,
                duration: 0.5,
                ease: 'power2.out',
              });
            },
          },
          '<'
        );
      });

      return;
    } else {
      gsap.context(() => {
        const tl = gsap.timeline();

        tl.to(titleRef.current, {
          text: secondTitle,
          duration: 1,
          ease: 'power2.out',
        });

        tl.to(descriptionRef.current, {
          text: description,
          duration: 1,
          ease: 'power2.out',
        });

        tl.to(ctaWrapperRef.current, {
          opacity: 1,
          duration: 1,
          ease: 'power2.out',
        });
      });
    }

    if (nextStepId) {
      goToNamedStep(nextStepId);
    } else {
      nextStep();
    }
  };

  const onBack = () => {
    if (firstCtaClick) {
      setFirstCtaClick(false);
      return;
    }
    previousStep();
  };

  return (
    <BaseStepV3 {...props}>
      <CopingWithNarcissismScreen imgDesktop={backgroundImageUrl} imgMobile={mobileViewBackgroundImageUrl}>
        <AbsoluteHeader onBack={onBack} color="white" withLogoOnMobileDisplay logoColor="white" />
        <CopingWithNarcissismScreenContentWrapper>
          <CopingWithNarcissismScreenTexts>
            <CopingWithNarcissismScreenTitle ref={titleRef}>
              {firstCtaClick ? secondTitle : title}
            </CopingWithNarcissismScreenTitle>
            {!firstCtaClick && (
              <CopingWithNarcissismScreenDescription ref={descriptionRef}>
                {description}
              </CopingWithNarcissismScreenDescription>
            )}
          </CopingWithNarcissismScreenTexts>
          <CopingWithNarcissismScreenCtaWrapper ref={ctaWrapperRef}>
            <StepV2ConfirmButton
              {...props}
              isButtonDisabled={false}
              isLoading={false}
              onClick={handleOnClick}
              ctaButtonTitle={ctaButtonTitle}
              variant={MainButtonVariant.White}
            ></StepV2ConfirmButton>
          </CopingWithNarcissismScreenCtaWrapper>
        </CopingWithNarcissismScreenContentWrapper>
      </CopingWithNarcissismScreen>
    </BaseStepV3>
  );
};
